import React from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import Spinner from "components/base/Spinner";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import ShipmentForm from "./Form";
import {
  FETCH_SHIPMENT,
  DELETE_SHIPMENT,
  UPDATE_SHIPMENT_NOTE,
} from "./graphql";
import { Alert } from "components/base/Toast";
import Button from "components/base/Button";
import { parseError } from "apollo";
import { PiTrashBold } from "react-icons/pi";

function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, data } = useQuery(FETCH_SHIPMENT, {
    variables: { id },
  });
  const [updateShipmentNote, updateShipmentNoteStatus] = useMutation(
    UPDATE_SHIPMENT_NOTE,
    {
      onCompleted: () => Alert("success", "Note saved."),
      onError(error) {
        Alert("error", parseError(error));
      },
    },
  );
  const [deleteShipment, deleteShipmentStatus] = useMutation(DELETE_SHIPMENT, {
    variables: { id },
    onCompleted: () => {
      Alert("success", "Shipment is deleted.");
      navigate(`/shipment/shipments/${location.search}`);
      window.location.reload();
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });
  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const { shipment, shipmentMeta } = data;
  const canSplit =
    shipment.isEffective &&
    !shipment.source &&
    shipment.children.edges.length === 0;

  function tryAddNote() {
    const note = window.prompt(
      "Simple note for this shipment, typically to clarify a duplicated shipment. It will show in the shipment list.",
      shipment.note || "",
    );
    if (note !== null) {
      updateShipmentNote({
        variables: {
          shipmentInput: { id: shipment.id, note },
        },
      });
    }
  }

  return (
    <Page
      className="lg:h-screen"
      backTo="/shipment/shipments"
      rightButtons={
        <div className="flex items-center gap-4">
          <Button
            onClick={tryAddNote}
            loading={updateShipmentNoteStatus.loading}
          >
            + Note
          </Button>
          {canSplit ? (
            <Button
              onClick={() =>
                navigate(`/shipment/shipments/${shipment.id}/split`)
              }
            >
              Split
            </Button>
          ) : null}

          <Button
            danger
            icon={<PiTrashBold size={16} />}
            aria-label="Delete shipment"
            title="Delete shipment"
            className="ml-2"
            loading={deleteShipmentStatus.loading}
            onClick={(_) => {
              if (window.confirm("You are sure to delete this shipment??!"))
                deleteShipment();
            }}
          >
            Delete
          </Button>
        </div>
      }
      title={`Shipment #${shipment.id}`}
      subtitle={`SO ${shipment.invoiceNumber} ${shipment.note ? " | " + shipment.note : ""}`}
    >
      {!shipment.isEffective && !shipment.source ? (
        <div className="mx-6 mt-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
          This is the original shipment retained as a reference after splitting.
        </div>
      ) : null}
      <ShipmentForm shipment={shipment} shipmentMeta={shipmentMeta} />
    </Page>
  );
}

export default ShipmentDetail;
